import {Directory, Encoding, Filesystem} from "@capacitor/filesystem";
import {StartUpload, emptyArray} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {TimeToString} from "web-vcore";
import {GetSessions} from "../../../../../Server/DB/sessions.js";
import {MeID} from "../../../../../Server/DB/users.js";
import {InAndroid, nativeBridge} from "../../../../../Utils/Bridge/Bridge_Native.js";
import {gdBelt_rawDataDB} from "../../../../../Utils/IndexedDB/GDBelt_RawDataDB.js";
import {WriteBreathDataToFile} from "../../../../Timeline/Sessions/SessionsUISettingsDropdown.js";
import {VirtualFileSystemViewer} from "./VirtualFileSystemViewer.js";

export const DataSubpanel = observer_mgl(()=>{
	const [includeMobXStore, setIncludeMobXStore] = useState(true);
	const [includeBreathData, setIncludeBreathData] = useState(true);
	const sessions = MeID() ? GetSessions(MeID()) : emptyArray;

	return (
		<>
			<Row mt={5} center>
				<Text>Data to include: </Text>
				<CheckBox ml={5} text="MobX store" value={includeMobXStore} onChange={val=>setIncludeMobXStore(val)}/>
				<CheckBox ml={5} text="Session breath-data" value={includeBreathData} onChange={val=>setIncludeBreathData(val)}/>
			</Row>
			<ExportRow includeMobXStore={includeMobXStore} includeBreathData={includeBreathData} sessions={sessions}/>
			<ImportRow/>
			<Row mt={5} center>
				<Button text="View virtual file-system" onClick={()=>{
					ShowMessageBox({
						title: "Virtual file-system",
						message: ()=><VirtualFileSystemViewer/>,
						cancelButton: true,
					});
				}}/>
			</Row>
		</>
	);
});

const ExportRow = observer_mgl((props: {includeMobXStore: boolean, includeBreathData: boolean, sessions: any[]})=>{
	const {includeMobXStore, includeBreathData, sessions} = props;
	const [progressMsg, setProgressMsg] = useState<string|n>(null);
	return (
		<Row mt={5} center>
			<Button text="Export to files" onClick={async()=>{
				setProgressMsg(null);
				const dateStr = TimeToString(Date.now(), {fileNameSafe: true});
				const folderRelPath = `LucidFrontier/DataExports/${dateStr}`;
				const createdFiles = [] as {name: string, size: number}[];

				if (includeMobXStore) {
					setProgressMsg("Exporting Store.json");
					const storeData = localStorage.getItem("__mobx_sync__") ?? "{}";
					await Filesystem.writeFile({
						path: `${folderRelPath}/Store.json`,
						data: storeData,
						directory: Directory.Documents,
						recursive: true,
						encoding: Encoding.UTF8,
					});
					createdFiles.push({name: "Store.json", size: storeData.length});
				}

				if (includeBreathData) {
					// write entry-by-entry to avoid OOM with large datasets
					const tempFileName = `BreathData_CountTBD.json`;
					const {count, totalBytes} = await WriteBreathDataToFile(
						sessions, `${folderRelPath}/${tempFileName}`, Directory.Documents,
						msg=>setProgressMsg(msg),
					);
					const fileName = `BreathData_Count${count}.json`;
					await Filesystem.rename({
						from: `${folderRelPath}/${tempFileName}`,
						to: `${folderRelPath}/${fileName}`,
						directory: Directory.Documents,
					});
					createdFiles.push({name: fileName, size: totalBytes});
				}

				setProgressMsg(null);
				const displayFolderPath = `Documents/${folderRelPath}`;
				ShowMessageBox({
					title: "Export complete",
					message: ()=><Column>
						<Row>Folder: {displayFolderPath}</Row>
						<Row mt={5}>Files created:</Row>
						{createdFiles.map((f, i)=>(
							<Row key={i} ml={10}>- {f.name} ({f.size.toLocaleString()} bytes)</Row>
						))}
					</Column>,
					extraButtons: ()=>{
						return <Row style={{display: "inline-flex", float: "right"}}>
							<Button text="Open folder" onClick={()=>{
								if (InAndroid(0)) {
									nativeBridge.Call("OpenLFDataExportSubfolder", dateStr);
								} else {
									ShowMessageBox({title: "Not supported", message: "Opening the folder is only supported on Android."});
								}
							}}/>
						</Row>;
					},
				});
			}}/>
			{progressMsg != null && <Text ml={10}>{progressMsg}</Text>}
		</Row>
	);
});

const ImportRow = observer_mgl(()=>{
	const [progressMsg, setProgressMsg] = useState<string|n>(null);
	return (
		<Row mt={5} center>
			<Button text="Import from files" onClick={async()=>{
				const proceed = async()=>{
					setProgressMsg(null);
					const files = await StartUpload(true);
					const importedFiles = [] as string[];
					let storeImported = false;
					let breathSessionsImported = 0;
					let breathChunksImported = 0;

					for (let fi = 0; fi < files.length; fi++) {
						const file = files[fi];
						setProgressMsg(`Reading file ${fi + 1} / ${files.length}: ${file.name}`);

						if (file.name == "Store.json") {
							const text = await file.text();
							localStorage.setItem("__mobx_sync__", text);
							storeImported = true;
							importedFiles.push(file.name);
						} else if (file.name.startsWith("BreathData_Count")) {
							const {sessionsImported, chunksImported} = await ImportBreathDataFile(
								file,
								msg=>setProgressMsg(`File ${fi + 1} / ${files.length}: ${msg}`),
							);
							breathSessionsImported += sessionsImported;
							breathChunksImported += chunksImported;
							importedFiles.push(file.name);
						}
					}

					setProgressMsg(null);
					ShowMessageBox({
						title: "Import complete",
						message: ()=><Column>
							{importedFiles.length == 0
								? <Row>No recognized files were selected. (expected Store.json or BreathData_Count*.json)</Row>
								: <>
									{storeImported && <Row>
										<Text>MobX store imported.</Text>
										<Text ml={5} style={{fontWeight: "bold"}}>(but must reload app now, eg. using button below, else changes will be lost)</Text>
									</Row>}
									{breathSessionsImported > 0 && <Row>Breath-data imported: {breathSessionsImported} sessions, {breathChunksImported.toLocaleString()} chunks.</Row>}
								</>}
						</Column>,
						extraButtons: ()=>{
							if (!storeImported) return null;
							return <Row style={{display: "inline-flex", float: "right"}}>
								<Button text="Reload now" onClick={()=>location.reload()}/>
							</Row>;
						},
					});
				};

				ShowMessageBox({
					title: "Import data from files (UNTESTED)", cancelButton: true,
					message: `
						* WARNING: The import function has not yet been tested by anyone!
						* Files to select: Store.json and/or BreathData_Count*.json (files exported by the app earlier)
						Proceed?
					`.AsMultiline(0),
					onOK: async()=>{
						await proceed();
					},
				});
			}}/>
			{progressMsg != null && <Text ml={10}>{progressMsg}</Text>}
		</Row>
	);
});

async function ImportBreathDataFile(file: File, onProgress: (msg: string)=>void): Promise<{sessionsImported: number, chunksImported: number}> {
	const reader = file.stream().pipeThrough(new TextDecoderStream()).getReader();
	let buf = "";
	let depth = 0;
	let inStr = false;
	let esc = false;
	let entryStart = -1; // index into buf where current top-level entry `{` was found
	let processedUpTo = 0;
	let sessionsImported = 0;
	let chunksImported = 0;

	while (true) {
		const {done, value} = await reader.read();
		if (value) buf += value;

		let i = processedUpTo;
		while (i < buf.length) {
			const c = buf[i];
			if (esc) { esc = false; i++; continue; }
			if (c === "\\" && inStr) { esc = true; i++; continue; }
			if (c === "\"") { inStr = !inStr; i++; continue; }
			if (inStr) { i++; continue; }

			if (c === "{" || c === "[") {
				if (depth === 1 && c === "{") entryStart = i; // start of a top-level entry
				depth++;
			} else if (c === "}" || c === "]") {
				depth--;
				if (depth === 1 && entryStart !== -1) {
					// completed a top-level entry object
					const entry = JSON.parse(buf.slice(entryStart, i + 1)) as {sessionId: string, chunks: any[]};
					onProgress(`Imported ${sessionsImported + 1} sessions...`);
					for (const chunk of entry.chunks) {
						await gdBelt_rawDataDB.StoreChunkForSession(chunk);
					}
					gdBelt_rawDataDB.NotifyDataChangedForSession(entry.sessionId);
					sessionsImported++;
					chunksImported += entry.chunks.length;
					// discard the processed portion of the buffer
					buf = buf.slice(i + 1);
					i = 0;
					processedUpTo = 0;
					entryStart = -1;
					continue;
				}
			}
			i++;
		}
		processedUpTo = i;
		if (done) break;
	}
	return {sessionsImported, chunksImported};
}